
export enum TaskStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  BLOCKED = 'BLOCKED'
}

export enum TimeUnit {
  QUARTER = '15m',
  HOUR = '1h',
  DAY = '1d',
  WEEK = '1w'
}

export enum TaskType {
  STANDARD = 'STANDARD',
  MILESTONE = 'MILESTONE',
  PLACEHOLDER = 'PLACEHOLDER'
}

export interface Session {
  id: string;
  name: string;
  description?: string;
}

export interface Meeting {
  id: string;
  projectId: string;
  title: string;
  date: string; // ISO String
  summary?: string;
  transcript?: string;
  attendees?: string[];
  createdAt: string;
}

export interface Task {
  id: string;
  name: string;
  description?: string;
  type: TaskType;
  duration: number; 
  unit: TimeUnit;
  sessionId?: string;
  meetingId?: string; // NEW: Provenance link to the meeting where this was decided
  dependencies: string[];
  status: TaskStatus;
  priority?: string;
  notes?: string;
  
  // Tactical Assessment (Database Fields)
  vectorX?: number; // For Radar Plotting
  vectorY?: number;
  riskLevel?: number; // 0-5
  strategicReward?: number; // 0-5
  probabilitySuccess?: number; // 0-1.0
  importance?: number; // 0-5
  budgetWeight?: number; // Float

  // Computed fields for CPM (Values in HOURS)
  earlyStart?: number;
  earlyFinish?: number;
  lateStart?: number;
  lateFinish?: number;
  slack?: number;
  isCritical?: boolean;
}

export interface ProjectState {
  id: string;
  name: string;
  description: string;
  sessions: Session[];
  tasks: Task[];
  startDate: string; // ISO Date string
  pendingMigrationSQL?: string; // NEW: Stores proposed SQL from AI
}

export enum AIIntent {
  INITIAL_SETUP = 'INITIAL_SETUP',
  STRUCTURAL_OPTIMIZATION = 'STRUCTURAL_OPTIMIZATION',
  RISK_MITIGATION = 'RISK_MITIGATION',
  FEATURE_EXPANSION = 'FEATURE_EXPANSION',
  UNDEFINED = 'UNDEFINED',
  DB_MIGRATION_PROPOSAL = 'DB_MIGRATION_PROPOSAL' // NEW
}

export enum AgentRole {
  ARCHITECT = 'Lead Architect',
  SW_ARCHITECT = 'Software Architect', // NEW: The High-Level Tech Lead
  RISK_ANALYST = 'Risk Analyst',
  DATA_MINER = 'Data Miner',
  STRATEGIST = 'Chief Strategist',
  DB_ARCHITECT = 'DB Architect' 
}

export interface Snapshot {
  id: string;
  timestamp: number;
  tagName: string; 
  intent: AIIntent;
  agentRole: AgentRole; 
  aiReasoning: string; 
  projectState: ProjectState;
}

export interface AIResponse {
  intent: AIIntent;
  tagName: string;
  reasoning: string;
  sessions: Session[];
  tasks: Task[];
  migrationSQL?: string; // NEW: The SQL payload
}

export interface ChatMessage {
  role: 'user' | 'model';
  agentRole?: AgentRole; 
  content: string;
  timestamp: number;
}

export interface DbConfig {
  host: string;
  port: string;
  database: string;
  user: string;
  password: string; 
  schema: string;
}

// Configuration for the external Notes DB (claude_conversations)
export interface NotesDbConfig {
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
}

export interface AppSettings {
  useBackend: boolean;
  backendUrl: string; 
  dbConfig?: DbConfig;
  notesDbConfig?: NotesDbConfig;
  agentApiKeys?: Record<string, string>; // NEW: Map AgentRole -> API Key
}

// Raw Note from the external system
export interface RawNote {
  id: number;
  created_at: string;
  content: string;
  fr_id: string;
  domain: string;
  note_type: string;
  priority: number;
  urgency: number;
  criticality: number;
  risk: number;
  reward: number;
  status: string;
}

export interface DBProject {
  id: string;
  name: string;
  description: string;
  lifecycle_stage: string;
  created_at: string;
}

// --- OPS COCKPIT CONTRACTS (Per Claude-dBMeister Spec) ---

export type OpsStatus = 'up' | 'down' | 'warning' | 'unknown' | 'maintenance' | 'syncing';

export interface OpsAction {
  id: string;
  label: string;
  confirm?: boolean; // If true, UI requires confirmation dialog
  danger?: boolean;  // If true, render in red
}

// 1. Checklist View (Center Panel)
export interface OpsChecklistItem {
  id: string;
  label: string;
  status: OpsStatus;
  detail: string;
  category: string;
  actions?: string[]; // IDs of available actions
}

export interface OpsChecklistView {
  view_type: 'checklist';
  title: string;
  timestamp: string;
  items: OpsChecklistItem[];
  summary: {
    total: number;
    up: number;
    warning: number;
    down: number;
  };
}

// 2. Tree View (Left Shoulder)
export interface OpsTreeNode {
  id: string;
  label: string;
  type: 'stream' | 'project' | 'service';
  status?: OpsStatus;
  color?: string; // Hex code hint
  children?: OpsTreeNode[];
}

export interface OpsTreeView {
  view_type: 'tree';
  root: string;
  nodes: OpsTreeNode[];
}

// 3. Detail View (Right Shoulder)
export interface OpsDetailProperty {
  label: string;
  value: string;
}

export interface OpsDetailView {
  view_type: 'detail';
  selected_id: string;
  title: string;
  status: OpsStatus;
  properties: OpsDetailProperty[];
  actions: OpsAction[];
  related_notes?: {
    fr_id: string;
    title: string;
  }[];
}

export type OpsViewData = OpsChecklistView | OpsTreeView | OpsDetailView;
